import { useState, useEffect, useCallback } from 'react';
import usePolling from './usePolling';
import { POLLING_INTERVALS } from '../config/polling';
import { logError, logWarning, getUserFriendlyErrorMessage } from '../utils/errorLogging';
import { authFetch } from '../utils/authFetch';

/**
 * Custom hook for fetching and managing problem data from the API.
 * Features:
 * - Initial load with full loading state
 * - Background refresh every 60 seconds (configurable)
 * - Separate refreshing state for subtle UI updates
 * - Preserves data during background refresh
 * - Error handling without data loss
 *
 * @returns {Object} - { problems: array, loading: boolean, refreshing: boolean, error: Error|null, refresh: function, lastUpdated: Date|null }
 */
const useProblems = () => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  /**
   * Fetch problems from API and transform the data structure.
   * Extracts first outage from each problem and flattens relevant properties.
   *
   * @param {boolean} isBackgroundRefresh - True if this is a background refresh (not initial load)
   */
  const fetchProblems = useCallback(async (isBackgroundRefresh = false) => {
    try {
      // Only show full loading spinner on initial load
      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Clear error only on background refresh (preserve error on initial load)
      if (isBackgroundRefresh) {
        setError(null);
      }

      const response = await authFetch('/api/v1/problem?category=open');

      if (response.status !== 200) {
        throw new Error('Unable to Fetch Problem Notes');
      }

      const result = await response.json();

      if (result.problems) {
        // Transform problems: extract first outage and flatten structure
        const transformedProblems = result.problems.map((p) => {
          // Get first outage key from the outages object (with null check)
          const outageKeys = Object.keys(p.outages || {});
          if (outageKeys.length === 0) return null;

          const firstOutageKey = outageKeys[0];
          const firstOutage = p.outages[firstOutageKey];

          // Extract affected brand - handle multiple brands/capabilities
          const brandParts = [];

          // Add main affected brand
          if (firstOutage.affectedBrand) {
            const brandName = typeof firstOutage.affectedBrand === 'object'
              ? firstOutage.affectedBrand.name
              : firstOutage.affectedBrand;
            if (brandName) brandParts.push(brandName);
          }

          // Add all capabilities (handle both array and single object)
          if (firstOutage.affectedBrandCapability) {
            const capabilities = Array.isArray(firstOutage.affectedBrandCapability)
              ? firstOutage.affectedBrandCapability
              : [firstOutage.affectedBrandCapability];

            capabilities.forEach(capability => {
              if (capability) {
                const capabilityName = typeof capability === 'object'
                  ? capability.name || capability.label || capability.value
                  : capability;
                if (capabilityName) brandParts.push(capabilityName);
              }
            });
          }

          // Join with commas for multiple brands
          const affectedBrand = brandParts.join(', ') || '';

          return {
            number: p.number,
            priority: firstOutage.priority,
            shortDescription: firstOutage.short_description,
            status: firstOutage.status,
            slackChannel: firstOutage.slackChannel,
            incidentStart: firstOutage.incidentStart,
            incidentEnd: firstOutage.incidentEnd,
            affectedBrand: affectedBrand,
          };
        }).filter(p => p !== null);

        setProblems(transformedProblems);
        setLastUpdated(new Date());
        setError(null); // Clear error on success
      } else {
        setProblems([]);
        setLastUpdated(new Date());
      }

      setIsInitialLoad(false);
    } catch (err) {
      // Use secure logging utility
      logError('useProblems.fetchProblems', err, {
        isBackgroundRefresh
      });

      // On background refresh error, keep old data and only log warning
      if (isBackgroundRefresh) {
        logWarning('useProblems.fetchProblems', 'Background refresh failed, keeping existing data');
        // Optional: Could track consecutive failures here
      } else {
        // On initial load error, show user-friendly error and clear data
        setError(new Error(getUserFriendlyErrorMessage(err)));
        setProblems([]);
      }
    } finally {
      if (isBackgroundRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchProblems(false); // Initial load
  }, [fetchProblems]);

  // Background auto-refresh using reusable polling hook
  usePolling(
    () => fetchProblems(true), // Background refresh
    POLLING_INTERVALS.INCIDENTS,
    {
      enabled: !isInitialLoad, // Only start polling after initial load
      pauseWhenHidden: true
    }
  );

  return {
    problems,
    loading,
    refreshing,
    error,
    lastUpdated,
    refresh: () => fetchProblems(true), // Manual refresh = background refresh
  };
};

export default useProblems;
